import { AppData, Lightning, Log, Router, Utils } from '@lightningjs/sdk'
import { AppSetup } from '../lib/app-setup';
import { Column } from '@lightningjs/ui-components';
import CCItem from './cc-item';
import CCItemContent from './cc-item-content'; // Assuming you want to reuse this or similar logic
import { Helper } from '../lib/helper';
import { DIALOG_BUTTON_ACTION, DIALOG_TYPE, ISO_LANGUAGE_MAP } from '../lib/enum';
import { IDialogueInfo } from '../lib/interfaces';

interface CCSettingsOverlayTemplateSpec extends Lightning.Component.TemplateSpecLoose {
    Overlay: {
        Background: object
        Title: object
        SettingsList: typeof Column
        OptionsPanel: typeof Column
    }
}

export default class CCSettingsOverlay extends Lightning.Component<CCSettingsOverlayTemplateSpec> {

    CCOptions: any[] = []
    CCItems: any[] = []
    CCItemsContent: any[] = []
    focusedList: 'SettingsList' | 'OptionsPanel' = 'SettingsList'
    _pendingLogAction: string | null = null

    static override _template(): Lightning.Component.Template<CCSettingsOverlayTemplateSpec> {
        return {
            w: 1920,
            h: 1080,
            rect: true,
            color: 0x00000000,
            Overlay: {
                w: 600,
                h: 1080,
                rect: true,
                color: 0xff0d0d1d,
                alpha: 0.9,
                BackButton: {
                    x: 60,
                    y: 107,
                    collision: true,
                    texture: {
                        src: Utils.asset('images/ico-back.png'),
                        resizeMode: {
                            h: 40,
                            w: 40,
                            type: 'contain',
                        },
                        type: Lightning.textures.ImageTexture
                    }
                },
                Title: {
                    x: 120,
                    y: 87,
                    text: {
                        text: 'Subtitles',
                        fontSize: 58,
                        textColor: 0xffffffff,
                        fontFace: Helper.getFontFamily('Black'),
                    },
                },
                SettingsList: {
                    x: 65,
                    y: 187,
                    w: 520,
                    h: 800,
                    collision: true,
                    type: Column,
                    clipping: true,
                    itemSpacing: 10,
                    scrollIndex: 4,
                    items: []
                },
                OptionsPanel: {
                    zIndex: 10,
                    alpha: 0,
                    x: (w: any) => w / 2 - 12,
                    y: (h: any) => h / 2,
                    mount: 0.5,
                    w: 445,
                    h: 200,
                    rect: true,
                    collision: true,
                    color: 0xff808080,
                    shader: { type: Lightning.shaders.RoundedRectangle, radius: 5 },
                    flex: { direction: 'column' },
                    HeaderWrapper: {
                        h: 70,
                        w: 445,
                        collision: true,
                        BackButton: {
                            x: 20,
                            y: 15,
                            collision: true,
                            alpha: 0.8,
                            texture: {
                                src: Utils.asset('images/ico-back.png'),
                                resizeMode: {
                                    h: 30,
                                    w: 30,
                                    type: 'contain',
                                },
                                type: Lightning.textures.ImageTexture
                            }
                        },
                        Header: {
                            x: 45,
                            y: 12,
                            text: {
                                text: '',
                                fontSize: 28,
                                textColor: 0xcc1c1c2b,
                                fontFace: Helper.getFontFamily('Black'),
                                textAlign: 'left',
                                wordWrapWidth: 405,
                                paddingLeft: 20
                            }
                        },
                    },
                    OptionsList: {
                        type: Column,
                        collision: true,
                        autoResizeHeight: true,
                        clipping: true,
                        style: {
                            itemSpacing: 10
                        },
                        items: []
                    }
                },
                OptionsBackground: {
                    w: 600,
                    h: 1080,
                    rect: true,
                    color: 0xff0d0d1d,
                    zIndex: 9,
                    alpha: 0,
                    collision: true,
                    visible: false
                }
            }
        } as any
    }

    override _init() {
        this.visible = false
        this.alpha = 0
    }

    override _active() {
        this.loadSettings()
        const { platform } = AppData as AppSetup
        const captionsSetting = this.CCOptions.find((setting: any) => setting.name === 'Captions')
        const activeOption = captionsSetting?.options?.find((opt: any) => opt.isActive)
        const captionsState = activeOption ? `, ${captionsSetting.name} ${activeOption.name}` : ''
        platform?.textToSpeach(`Subtitles settings${captionsState}`)
    }

    loadSettings() {
        const { ccOptions } = AppData as AppSetup
        this.CCOptions = ccOptions.userConfig

        this.CCItems = this.CCOptions.map((item, index) => {
            return {
                type: CCItem,
                w: 445,
                h: 56 + 60,
                item: item,
                itemIndex: index,
                itemSelected: false
            }
        })
        this.tag('SettingsList')!.items = this.CCItems
    }

    showOptions(itemIndex: number) {
        // Load options for the selected setting
        const setting = this.CCOptions[itemIndex]

        // Handle "Reset Default" separately
        if (setting.name === 'Reset Default Caption Settings') {
            this.verifyResetDefault(this._handleResetCallback.bind(this))
            return
        }

        if (!setting.options) return;

        // Set Header
        this.tag('OptionsPanel.Header').text.text = setting.name

        const settingList = this.tag('Overlay.SettingsList') as unknown as Column
        if (settingList) {
            settingList.selectedIndex = itemIndex;
        }

        let activeIndex = 0;
        let activeOptionName = ''
        this.CCItemsContent = setting.options.map((opt: any, index: number) => {
            if (opt.isActive) {
                activeIndex = index;
                activeOptionName = opt.name;
            }
            return {
                type: CCItemContent,
                w: 445,
                h: 64,
                settingName: setting.name,
                item: opt,
                itemIndex: index,
                itemSelected: opt.isActive
            }
        })

        const { platform } = AppData as AppSetup
        platform?.textToSpeach(`${setting.name}${activeOptionName ? ', ' + activeOptionName : ''}`)

        const optionsList = this.tag('Overlay.OptionsPanel.OptionsList') as unknown as Column
        if (optionsList) {
            optionsList.items = this.CCItemsContent
            optionsList.selectedIndex = activeIndex;
        }
        
        const contentHeight = (this.CCItemsContent.length * 64) + ((this.CCItemsContent.length - 1) * 10)
        const totalHeight = contentHeight + 70
        this.tag('OptionsPanel').h = totalHeight

        this.focusedList = 'OptionsPanel'
        this.tag('Overlay.OptionsBackground')!.visible = true
        this.tag('Overlay.OptionsBackground')!.alpha = 0.95
        this.tag('Overlay.OptionsPanel')!.alpha = 1
        this.tag('Overlay.OptionsPanel')!.zIndex = 41
        this.tag('Overlay.SettingsList')!.alpha = 0.8

        if (settingList && settingList.items) {
            settingList.items.forEach((item: any) => {
                item.isOpened = true;
            });
        }

        this._refocus() 
    }

    hideOptions() {
        this.focusedList = 'SettingsList'

        this.tag('Overlay.OptionsBackground')!.visible = false
        this.tag('Overlay.OptionsBackground')!.alpha = 0
        this.tag('Overlay.OptionsPanel')!.alpha = 0
        this.tag('Overlay.OptionsPanel')!.zIndex = 10
        this.tag('Overlay.SettingsList')!.alpha = 1

        const settingList = this.tag('Overlay.SettingsList') as unknown as Column
        if (settingList && settingList.items) {
            settingList.items.forEach((item: any) => {
                item.isOpened = false;
            });
        }
        
        if (this._pendingLogAction) {
            this.fireAncestors('$ccLogAction' as any, this._pendingLogAction);
            this._pendingLogAction = null;
        }

        this._refocus()
    }

    updateCaption(optionIndex: number) {
        const settingList = this.tag('Overlay.SettingsList') as unknown as Column
        const settingIndex = settingList.selectedIndex
        const setting = this.CCOptions[settingIndex]

        // Update local object
        setting.options.forEach((opt: any, index: number) => {
            opt.isActive = (index === optionIndex)
        })

        // Update value for top level item
        const selectedOption = setting.options[optionIndex]
        setting.value = selectedOption.value

        // Save and Update
        const { ccOptions } = AppData as AppSetup
        ccOptions.saveSettings()
        ccOptions.updateCSS()

        // Check for Captions toggle
        if (setting.name === 'Captions') {
            this.fireAncestors('$ccToggle' as any, setting.value)
        }

        // Check for Language change — notify video screen to swap CC track
        if (setting.name === 'Language') {
            this.fireAncestors('$ccLanguageChange' as any, setting.value)
            const englishLangNameRaw = ISO_LANGUAGE_MAP[selectedOption.value as keyof typeof ISO_LANGUAGE_MAP] || selectedOption.name
            this._pendingLogAction = `CCLanguageChanged-"${englishLangNameRaw}"`
        } else if (setting.name === 'Character Size') {
            this._pendingLogAction = `CCFontSizeChanged-"${selectedOption.name}"`
        } else if (setting.name === 'Character Color') {
            this._pendingLogAction = `CCFontColorChanged-"${selectedOption.name}"`
        }

        // Refresh UI
        this.loadSettings()
        // Restore SettingsList focus index as loadSettings might reset it
        // We need to wait for items to be set? Usually synchronous in Lightning if just setting items property.
        settingList.selectedIndex = settingIndex
        
        // Refresh Options Panel to show new checkmark
        this.showOptions(settingIndex)
        
        // Restore OptionsList focus index
        const optionsList = this.tag('Overlay.OptionsPanel.OptionsList') as unknown as Column
        if (optionsList) {
            optionsList.selectedIndex = optionIndex
        }
    }

    override _getFocused(): any {
        if (this.focusedList === 'SettingsList') {
            return this.tag('Overlay.SettingsList')
        } else {
            return this.tag('Overlay.OptionsPanel.OptionsList')
        }
    }

    override _handleBack() {
        if (this.focusedList === 'OptionsPanel') {
            this.hideOptions()
            return true
        } else {
            this.close()
            return true
        }
    }

    _handleClick(target: any) {
        console.log('CCOverlay _handleClick', target);
        if (target.ref === 'BackButton') {
            this.close()
        } else if (target.ref === 'OptionsPanel' || target.ref === 'OptionsBackground') {
            this.hideOptions()
        }
    }

    close() {
        const { platform } = AppData as AppSetup
        platform?.textToSpeach('Closing subtitle settings')
        if (this._pendingLogAction) {
            this.fireAncestors('$ccLogAction' as any, this._pendingLogAction);
            this._pendingLogAction = null;
        }
        this.visible = false
        this.alpha = 0
        this.fireAncestors('$closeCCOverlay' as any)
    }

    $ItemSelected(itemIndex: number) {
        this.showOptions(itemIndex)
    }

    $ItemHovered(itemIndex: number) {
        if (this.focusedList === 'SettingsList') {
            const list = this.tag('Overlay.SettingsList') as unknown as Column
            if (list) {
                list.selectedIndex = itemIndex;
                this._refocus();
            }
        }
    }

    $optionSelected(optionIndex: number) {
        this.updateCaption(optionIndex)
    }

    $OptionHovered(optionIndex: number) {
        if (this.focusedList === 'OptionsPanel') {
            const list = this.tag('Overlay.OptionsPanel.OptionsList') as unknown as Column
            if (list) {
                list.selectedIndex = optionIndex;
                this._refocus();
            }
        }
    }

    verifyResetDefault(callback?: any) {
        const dialogInfo: IDialogueInfo = {
            title: 'Are you sure you want to proceed?',
            body: '',
            type: DIALOG_TYPE.APP_EXIT_BOX,
            buttons: [
                {
                    w: 200,
                    name: 'No',
                    index: 0,
                    action: DIALOG_BUTTON_ACTION.NO,
                    initialFocus: true,
                },
                {
                    w: 200,
                    name: 'Yes',
                    index: 1,
                    action: DIALOG_BUTTON_ACTION.YES,
                    initialFocus: false
                }
            ]
        }
        if (callback) {
            dialogInfo.callback = callback
        }
        const page: any = Router.getActivePage()
        if (page.widgets && page.widgets.dialog) {
            page.widgets.dialog.showDialog(dialogInfo)
            Router.focusWidget('dialog')
        }
        return true
    }

    _handleResetCallback(actionBtn: any, dialogInfo: IDialogueInfo) {
        if (actionBtn.action == DIALOG_BUTTON_ACTION.YES) {
            const { ccOptions } = AppData as AppSetup
            ccOptions.resetDefault()
            this.loadSettings() // Refresh UI
            // Ensure On/Off toggle is synced if it was changed
            const setting = this.CCOptions.find(s => s.name === 'Captions');
            if (setting) {
                this.fireAncestors('$ccToggle' as any, setting.value)
            }
            const langSetting = this.CCOptions.find(s => s.name === 'Language');
            if (langSetting) {
                this.fireAncestors('$ccLanguageChange' as any, langSetting.value)
            }
        }
        // Focus back to this component whether YES or NO
        Router.focusPage()
        this._refocus()
    }

    _handleScroll(obj: any) {
        if (obj.deltaY === 0) return

        const direction = obj.deltaY > 0 ? 1 : -1
        let activeList: any

        if (this.focusedList === 'SettingsList') {
            activeList = this.tag('Overlay.SettingsList')
        } else {
            activeList = this.tag('Overlay.OptionsPanel.OptionsList')
        }

        if (activeList && activeList.items && activeList.items.length > 0) {
            const newIndex = activeList.selectedIndex + direction
            if (newIndex >= 0 && newIndex < activeList.items.length) {
                activeList.selectedIndex = newIndex
            }
        }
    }
}
